import React, { useEffect, useRef, useState } from "react"
import { graphql } from "gatsby"
import { Trans, useTranslation } from "@zestia/gatsby-plugin-react-i18next"
import findImage from "@/utils/find-image"

import MainLayout from "@/layouts/MainLayout"

import SharedHead from "@/components/SharedHead"
import FeatureHero from "@/components/FeatureHero"
import SplitPane from "@/components/SplitPane"
import FeaturesWithCallToAction from "@/components/FeaturesWithCallToAction"
import QuoteLarge from "@/components/Quote"
import IntegrationsShowcase from "@/components/IntegrationsShowcase"
import PricingSnapshot from "@/components/PricingSnapshot"

import contactHistoryVideo from "@/videos/contact-history.mp4"
import opportunityMilestoneVideo from "@/videos/opportunity-milestone.mp4"
import salesAnalyticsVideo from "@/videos/sales-analytics-reports.mp4"
import workflowAutomationVideo from "@/videos/onboarding-automation.mp4"

import { setOfferCookie } from "@/utils/cookie-detection"

export const Head = props => {
  return <SharedHead {...props} noIndex={true} />
}

const QuickBooksPage = ({ location, data }) => {
  const { t } = useTranslation()

  const [tier, setTier] = useState()
  const [frequency, setFrequency] = useState("MONTHLY")

  useEffect(() => {
    setOfferCookie("20% LIFETIME")
  }, [tier])

  const plans = useRef()

  const jumpToPlanColumns = () => {
    plans.current.scrollIntoView({ behavior: "smooth" })
  }

  return (
    <MainLayout showNavLinks={false} showLogIn={false} showSignUp={false}>
      <FeatureHero
        image={{
          src: findImage(data.allFile, "hero"),
          alt: "",
        }}
      >
        <h1 className="mb-0 pt-10 sm:pt-0">
          {t("quickbooks.campaign.heading")}
        </h1>

        <p className="mb-8 mt-10 p--lg col-md-8">
          <Trans
            i18nKey={"quickbooks.campaign.subheading"}
            components={{
              strong: <strong />,
            }}
          />
        </p>
      </FeatureHero>

      <section className="pb-20" ref={plans}>
        <PricingSnapshot
          frequency={frequency}
          onSelectPlan={setTier}
          onTogglePlanFrequency={setFrequency}
          showFrequencyToggle={true}
          tier={tier}
          location={location}
          closeModal={() => setTier(null)}
        />
      </section>

      <SplitPane
        className="mt-20"
        assetAlignment="stacked"
        title={t("campaign.simple-yet-powerful")}
      />

      <SplitPane
        assetAlignment="left"
        video={contactHistoryVideo}
        kicker={t("campaign.contactManagement.kicker")}
        title={t("campaign.contactManagement.title")}
        text={t("campaign.contactManagement.text")}
      />

      <SplitPane
        assetAlignment="right"
        video={opportunityMilestoneVideo}
        kicker={t("campaign.salesPipeline.kicker")}
        title={t("campaign.salesPipeline.title")}
        text={t("campaign.salesPipeline.text")}
      />

      <SplitPane
        assetAlignment="left"
        video={salesAnalyticsVideo}
        kicker={t("campaign.salesAnalytics.kicker")}
        title={t("campaign.salesAnalytics.title")}
        text={t("campaign.salesAnalytics.text")}
      />

      <SplitPane
        assetAlignment="right"
        video={workflowAutomationVideo}
        kicker={t("campaign.workflowAutomation.kicker")}
        title={t("campaign.workflowAutomation.title")}
        text={t("campaign.workflowAutomation.text")}
      />

      <FeaturesWithCallToAction
        title={t("campaign.cta.title")}
        showCta={false}
      />

      <IntegrationsShowcase
        title={t("campaign.integration-highlight.title")}
        description={t("campaign.integration-highlight.description")}
      />

      <QuoteLarge
        quote={t("campaign.quote.text")}
        cite={{
          name: "Dale Harris",
          role_company: t("campaign.quote.cite.role"),
        }}
      />
    </MainLayout>
  )
}

export default QuickBooksPage

export const query = graphql`
  query ($language: String) {
    ...Locales
    ...Integrations
    allFile(
      filter: {
        sourceInstanceName: { eq: "images" }
        relativeDirectory: { eq: "campaign/quickbooks" }
      }
    ) {
      edges {
        node {
          childImageSharp {
            gatsbyImageData
          }
        }
      }
    }
  }
`
